import type { Slide } from "pages/media/types";

// Constantes de animación
export const ANIMATION = {
  WORD_DELAY: 0.08, // Delay entre palabras
  WORD_FADE_DURATION: 0.25, // Duración del fade de cada palabra
  ELEMENT_GAP: 0.15, // Pausa entre elementos dentro de un slide
  SLIDE_GAP: 0.3, // Pausa entre slides
  SECTION_GAP: 0.2, // Pausa entre secciones
  SKELETON_DURATION: 0.3, // Duración de animación del skeleton
  FIGURE_ANIMATION: 0.5, // Duración de animación de figura
  IMAGE_LOAD_ESTIMATE: 0.5, // Estimación para carga de imagen
  VOICEOVER_GAP: 0.2, // Pausa entre slide y voiceover
  THINKING_TEXT_GAP: 0.4, // Pausa entre textos de thinking process
};

// Calcula la duración del typewriter para un texto
export const getTypewriterDuration = (text: string | undefined): number => {
  if (!text) return 0;
  const wordCount = text.split(" ").length;
  if (wordCount === 0) return 0;
  return (wordCount - 1) * ANIMATION.WORD_DELAY + ANIMATION.WORD_FADE_DURATION;
};

// Calcula la duración total de un slide según su tipo
export const getSlideDuration = (slide: Slide): number => {
  const { slide_type, placeholders } = slide;

  switch (slide_type) {
    case "title": {
      const titleDuration = getTypewriterDuration(placeholders.title);
      const subtitleDuration = getTypewriterDuration(placeholders.subtitle);
      return titleDuration + ANIMATION.ELEMENT_GAP + subtitleDuration;
    }

    case "bullet_point": {
      const titleDuration = getTypewriterDuration(placeholders.title);
      const bullets = placeholders.bullet_points ?? [];
      let bulletsDuration = 0;
      bullets.forEach((bullet) => {
        bulletsDuration +=
          getTypewriterDuration(bullet) + ANIMATION.ELEMENT_GAP;
      });
      return titleDuration + ANIMATION.ELEMENT_GAP + bulletsDuration;
    }

    case "quote": {
      return getTypewriterDuration(placeholders.quote);
    }

    case "figure": {
      const figure = placeholders.figure as {
        short_description: string;
      };
      const titleDuration = getTypewriterDuration(
        (placeholders as Record<string, unknown>).slide_title as string
      );
      const descDuration = getTypewriterDuration(figure?.short_description);
      return (
        titleDuration +
        ANIMATION.ELEMENT_GAP +
        ANIMATION.FIGURE_ANIMATION +
        ANIMATION.ELEMENT_GAP +
        descDuration
      );
    }

    case "image": {
      const titleDuration = getTypewriterDuration(placeholders.title);
      return titleDuration + ANIMATION.IMAGE_LOAD_ESTIMATE;
    }

    default:
      return 1; // Default fallback
  }
};

// Calcula delays acumulados para un array de slides
export const calculateSlideDelays = (slides: Slide[]): number[] => {
  const delays: number[] = [];
  let accumulatedDelay = 0;

  slides.forEach((slide) => {
    delays.push(accumulatedDelay);
    accumulatedDelay += getSlideDuration(slide) + ANIMATION.SLIDE_GAP;
  });

  return delays;
};

// Calcula delays acumulados incluyendo voiceover para cada slide
// animatedSlides: Set de slide IDs que ya fueron animados (su duración efectiva es 0)
export const calculateSlideDelaysWithVoiceover = (
  slides: Slide[],
  voiceoverScripts: string[],
  animatedSlides?: Set<string>
): number[] => {
  const delays: number[] = [];
  let accumulatedDelay = 0;

  slides.forEach((slide, index) => {
    delays.push(accumulatedDelay);

    // Si el slide ya fue animado, su duración efectiva es 0
    const alreadyAnimated = animatedSlides?.has(slide.uuid) ?? false;
    if (alreadyAnimated) {
      return; // No suma tiempo, el siguiente slide empieza inmediatamente
    }

    // Duración del slide
    const slideDuration = getSlideDuration(slide);

    // Duración del voiceover (si existe)
    const voiceoverScript = voiceoverScripts[index] ?? "";
    const voiceoverDuration = voiceoverScript
      ? ANIMATION.VOICEOVER_GAP + getTypewriterDuration(voiceoverScript)
      : 0;

    // El siguiente slide empieza después del slide + voiceover + gap
    accumulatedDelay += slideDuration + voiceoverDuration + ANIMATION.SLIDE_GAP;
  });

  return delays;
};

// Calcula la duración total de todos los slides + voiceovers de una sección
// animatedSlides: Set de slide IDs que ya fueron animados (su duración efectiva es 0)
export const getSectionSlidesDuration = (
  slides: Slide[],
  voiceoverScripts: string[],
  animatedSlides?: Set<string>
): number => {
  let totalDuration = 0;

  slides.forEach((slide, index) => {
    // Si el slide ya fue animado, no suma tiempo
    const alreadyAnimated = animatedSlides?.has(slide.uuid) ?? false;
    if (alreadyAnimated) {
      return;
    }

    const slideDuration = getSlideDuration(slide);
    const voiceoverScript = voiceoverScripts[index] ?? "";
    const voiceoverDuration = voiceoverScript
      ? ANIMATION.VOICEOVER_GAP + getTypewriterDuration(voiceoverScript)
      : 0;

    totalDuration += slideDuration + voiceoverDuration + ANIMATION.SLIDE_GAP;
  });

  return totalDuration;
};
